import produce from 'immer';
import { SearchResult } from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { MetaConversation } from 'lib-shared-legacy-types';
import type { Channel } from 'lib-shared-types-channels';

import { getConversationTitle } from 'app/utils/conversationTitle';
import { ConversationMetadata } from 'components/conversation';
import { ConversationSummary } from 'components/conversation/components/conversationSummary/conversationSummary';
import { getReplyWindowExpiresOnFromConversation } from 'components/conversation/utils/getReplyWindowExpiresOnFromConversation';
import { getUserDisplayName } from 'utils/user';

interface ConversationLegacyPreviewHeadlineProps {
  readonly metaConversation: MetaConversation;
  highlights?: SearchResult<'conversations'>['highlights'];
}

export const ConversationLegacyPreviewHeadline = ({
  metaConversation: metaConversationReadOnly,
  highlights,
}: ConversationLegacyPreviewHeadlineProps): JSX.Element => {
  const subject =
    metaConversationReadOnly?.conversation?.attributes?.subject || '';
  const highlightedTitleString = highlights?.subject?.[0] ?? subject;

  const metaConversation = produce(metaConversationReadOnly, (draft) => {
    if (!draft.conversation.attributes) {
      draft.conversation.attributes = {};
    }
    draft.conversation.attributes.subject = highlightedTitleString;
  });

  const conversation = metaConversation?.conversation;
  const { requester } = metaConversation;

  const title = getConversationTitle(metaConversation) ?? '';
  const requesterName = requester ? getUserDisplayName(requester) : undefined;

  const replyWindowExpiresOn =
    getReplyWindowExpiresOnFromConversation(metaConversation);

  const csid = conversation?.id;

  return (
    <div className="conversation-headline-desc">
      <ConversationSummary
        replyWindowExpiresOn={replyWindowExpiresOn}
        conversationInfo={{
          id: conversation.id,
          initial_channel: conversation.initial_channel,
          status: conversation.status,
          direction: conversation.direction,
          created_at: conversation.created_at,
          closed_at: conversation.closed_at,
          pending_expiration_at: conversation.pending_expiration_at,
        }}
        conversationAttributes={metaConversation.conversation.attributes}
        labels={{
          title: conversation.is_abandoned ? `Abandoned: ${title}` : title,
          requester: requesterName,
          queue: conversation.queue?.name ?? '',
        }}
        loading={csid ? false : true}
        dropdown={
          csid ? (
            <ConversationMetadata
              csid={csid}
              channel={
                conversation.initial_channel &&
                (convertChannelCasing(
                  conversation.initial_channel,
                  'snake',
                ) as Channel)
              }
              direction={conversation.direction}
              status={conversation.status}
              isAbandoned={conversation.is_abandoned}
              queueName={conversation.queue?.name}
              createdAt={conversation.created_at}
              closedAt={conversation.closed_at}
              attributes={metaConversationReadOnly.conversation.attributes}
            />
          ) : (
            <ConversationMetadata isLoading />
          )
        }
      />
    </div>
  );
};
